import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { getProfileName, getInvestingProfileName } from "../../Profiles/utils";
import * as styles from "./styles_vanilla.css";

type EscalationType = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

type Props = {
  caseId: number;
  escalations?: EscalationType[] | null;
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  onSelect: (id: number) => void;
  linkMode?: boolean;
  linkUrl?: string;
  successMode?: boolean;
  editMode?: boolean;
};

export const EscalationsMenu = ({
  caseId,
  escalations,
  profilesForCase,
  onSelect,
  linkMode,
  linkUrl,
  successMode,
  editMode,
}: Props) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isPicrCase =
    (profilesForCase?.caseType as CaseType | undefined) ===
    CaseType.PaymentInstructionChangeReview;

  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: { caseId },
    skip: !isPicrCase,
  });

  const options = useMemo(() => {
    const profileNameById = new Map<number, string>();
    const profiles = profilesForCase?.profiles ?? [];
    for (const p of profiles) {
      if (p) {
        profileNameById.set(p.id, getProfileName(p));
      }
    }
    const root = profilesForCase?.investingProfile as unknown as
      { id?: number | null } | undefined;
    if (root && typeof root.id === "number") {
      const rootName = getInvestingProfileName(profilesForCase?.investingProfile);
      profileNameById.set(root.id, rootName);
    }

    if (isPicrCase && picrPositionData?.picrPositionDetailsByCaseId) {
      for (const pos of picrPositionData.picrPositionDetailsByCaseId) {
        const created = (pos as { createdProfile?: { id?: number } | null }).createdProfile as
          Parameters<typeof getProfileName>[0] | undefined;
        const createdId = (created as { id?: number } | undefined)?.id;
        if (created && typeof createdId === "number") {
          profileNameById.set(createdId, getProfileName(created));
        }

        const linkedCase = (
          pos as {
            linkedOnboardingCase?: { profiles?: Array<{ id?: number } | null> | null } | null;
          }
        ).linkedOnboardingCase;
        if (linkedCase?.profiles) {
          for (const linkedCaseProfile of linkedCase.profiles) {
            if (linkedCaseProfile?.id) {
              profileNameById.set(
                linkedCaseProfile.id,
                getProfileName(linkedCaseProfile as Parameters<typeof getProfileName>[0]),
              );
            }
          }
        }

        const linkedInvesting = (
          pos as {
            linkedOnboardingCase?: { investingProfile?: ProfileNameFields | null } | null;
          }
        ).linkedOnboardingCase?.investingProfile;
        const linkedId = (linkedInvesting as { id?: number } | undefined)?.id;
        if (linkedInvesting && typeof linkedId === "number") {
          profileNameById.set(linkedId, getInvestingProfileName(linkedInvesting));
        }
      }
    }

    return (escalations ?? [])
      .filter((e) => e)
      .map((e) => {
        const esc = e as { id: number; profileId?: number | null; description?: string };
        const pid = esc.profileId;
        const labelFromId = typeof pid === "number" ? profileNameById.get(pid) : undefined;
        const label =
          labelFromId ||
          (typeof pid !== "number" && root && typeof root.id === "number"
            ? profileNameById.get(root.id)
            : undefined) ||
          esc.description ||
          `Escalation #${esc.id}`;
        return { value: esc.id, label };
      });
  }, [escalations, profilesForCase, isPicrCase, picrPositionData]);

  const handleOutsideClick = useCallback((evt: MouseEvent) => {
    if (!wrapperRef.current) return;
    if (!wrapperRef.current.contains(evt.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, handleOutsideClick]);

  if (!options.length) return null;

  const isSingleEscalation = options.length === 1;
  const pluralRules = new Intl.PluralRules("en-US");
  const pluralForm = pluralRules.select(options.length);
  const escalationText = `${options.length} Escalation${pluralForm === "one" ? "" : "s"}`;

  return (
    <div ref={wrapperRef} className={styles.menuWrapper}>
      <button
        type="button"
        className={successMode ? styles.triggerSuccess : styles.trigger}
        onClick={() => {
          if (linkMode && linkUrl) {
            window.open(linkUrl, "_blank");
            return;
          }
          if (!linkMode && isSingleEscalation) {
            onSelect(options[0].value);
            return;
          }
          setOpen((v) => !v);
        }}
      >
        <span className={successMode ? styles.circleIconSuccess : styles.circleIcon}>
          <Icon type={successMode ? "check" : "arrow-up"} />
        </span>
        <Text className={successMode ? styles.triggerTextSuccess : styles.triggerText}>
          {escalationText}
        </Text>
        <Icon
          type={
            linkMode
              ? "external-link"
              : !linkMode && isSingleEscalation
                ? editMode
                  ? "edit"
                  : "eye"
                : "chevron-down"
          }
          className={successMode ? styles.chevronSuccess : styles.chevron}
        />
      </button>
      {!linkMode && open && (
        <div className={styles.menu} role="menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setOpen(false);
                onSelect(opt.value);
              }}
            >
              <Flex alignItems="center">
                <Text>{opt.label}</Text>
              </Flex>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
